import { useMemo, useState, type ReactNode } from 'react';
import type { Device, ShowEvent } from '@glyph/protocol-ts';
import { useDevices, useEvents, type ShowEventIn } from '@glyph/core';
import { Button, Card, CellMono, CellText, CellTitle, Checkbox, DataTable, Dialog, EmptyState, IconButton, PageHeader, Section, StatusPill, TextArea, TextInput, useToast } from '@glyph/ui';
import { fmtDateTs } from './shared';

const toLocalInput = (ts: number) => {
  const d = new Date(ts * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v: string) => Math.floor(new Date(v).getTime() / 1000);
const blank = (): ShowEventIn => {
  const now = Math.floor(Date.now() / 1000);
  return { name: '', venue: '', description: '', tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', starts_at: now, ends_at: now + 8 * 3600 };
};
/** live: inside its window · upcoming: window not started · ended: window over (drops off the sidebar). */
export const groupState = (e: ShowEvent, now = Date.now() / 1000) => (e.ends_at < now ? 'ended' : e.starts_at > now ? 'upcoming' : 'live');

export interface GroupsPanelProps {
  /** Open a group's own page (its units and alerts). */
  onOpenGroup?: (id: string) => void;
  /** Inside the frosted dialog: no page header, the New group button sits above the list. */
  embedded?: boolean;
  /** Extra header actions (the admin tab switcher). */
  tabs?: ReactNode;
}

/** Groups (shows): list, New / Edit (name, description, venue, active window), Units on it, Delete. */
export function GroupsPanel({ onOpenGroup, embedded, tabs }: GroupsPanelProps) {
  const events = useEvents();
  const devices = useDevices();
  const toast = useToast();
  const [editing, setEditing] = useState<{ id: string | null; body: ShowEventIn } | null>(null);
  const [assigning, setAssigning] = useState<ShowEvent | null>(null);
  const [removing, setRemoving] = useState<ShowEvent | null>(null);
  const list = useMemo(() => [...(events.data ?? [])].sort((a, b) => b.starts_at - a.starts_at), [events.data]);
  const devs = devices.data ?? [];
  const unitsOn = (e: ShowEvent) => devs.filter((d) => d.assignment?.event_id === e.id);
  const save = async () => {
    if (!editing || !editing.body.name.trim()) return;
    if (editing.body.ends_at <= editing.body.starts_at) {
      toast.push('End must be after start', 'warn');
      return;
    }
    try {
      if (editing.id) await events.update({ id: editing.id, body: editing.body });
      else await events.create(editing.body);
      setEditing(null);
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const remove = async () => {
    if (!removing) return;
    try {
      await events.remove(removing.id);
      setRemoving(null);
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const toggle = async (d: Device, on: boolean) => {
    if (!assigning) return;
    try {
      await events.assign({ deviceId: d.id, eventId: on ? assigning.id : null });
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const edit = (e: ShowEvent) => setEditing({ id: e.id, body: { name: e.name, venue: e.venue, description: e.description ?? '', tz: e.tz, starts_at: e.starts_at, ends_at: e.ends_at } });
  const newGroup = (
    <Button variant="primary" size="responsive" onClick={() => setEditing({ id: null, body: blank() })}>
      New group
    </Button>
  );
  const set = (patch: Partial<ShowEventIn>) => setEditing((s) => (s ? { ...s, body: { ...s.body, ...patch } } : s));
  const table = (
    <DataTable
      rows={list}
      rowKey={(e) => e.id}
      onRowClick={onOpenGroup ? (e) => onOpenGroup(e.id) : undefined}
      minWidth={embedded ? 640 : 760}
      hideOnMobile={false}
      empty={<EmptyState label="No groups yet" action={newGroup} />}
      columns={[
        {
          key: 'name',
          header: 'Group',
          width: '1.8fr',
          cell: (e) => <CellTitle title={e.name} sub={[e.venue, e.description].filter(Boolean).join(' · ') || '—'} />,
        },
        { key: 'state', header: 'State', width: '.7fr', cell: (e) => <StatusPill state={groupState(e) === 'live' ? 'live' : groupState(e) === 'ended' ? 'offline' : 'standby'}>{groupState(e)}</StatusPill> },
        { key: 'when', header: 'Active', width: '1.5fr', cell: (e) => <CellMono>{fmtDateTs(e.starts_at)} → {fmtDateTs(e.ends_at)}</CellMono> },
        { key: 'units', header: 'Units', width: '1.1fr', cell: (e) => <CellText>{unitsOn(e).map((d) => d.name).join(', ') || '—'}</CellText> },
        {
          key: 'act',
          header: '',
          width: '190px',
          align: 'right',
          cell: (e) => (
            <div className="flex justify-end gap-1.5" onClick={(ev) => ev.stopPropagation()}>
              <Button size="sm" onClick={() => setAssigning(e)}>
                Units
              </Button>
              <Button size="sm" onClick={() => edit(e)}>
                Edit
              </Button>
              <IconButton framed={false} danger label={`Delete ${e.name}`} className="text-xl text-fg5" onClick={() => setRemoving(e)}>
                ×
              </IconButton>
            </div>
          ),
        },
      ]}
    />
  );
  const dialogs = (
    <>
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit group' : 'New group'}
        meta="NAME · DESCRIPTION · VENUE · ACTIVE WINDOW"
        size="lg"
        confirm={{ label: editing?.id ? 'Save' : 'Create', onClick: save, disabled: !editing?.body.name.trim(), busy: events.busy }}
      >
        {editing && (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Name</span>
              <TextInput value={editing.body.name} onChange={(e) => set({ name: e.target.value })} block autoFocus />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Description</span>
              <TextArea value={editing.body.description} onChange={(e) => set({ description: e.target.value })} block placeholder="What this group is for, where it runs, who looks after it" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="micro text-fg3">Venue</span>
              <TextInput value={editing.body.venue} onChange={(e) => set({ venue: e.target.value })} block />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-2">
                <span className="micro text-fg3">Active from</span>
                <TextInput type="datetime-local" value={toLocalInput(editing.body.starts_at)} onChange={(e) => e.target.value && set({ starts_at: fromLocalInput(e.target.value) })} block />
              </label>
              <label className="flex flex-col gap-2">
                <span className="micro text-fg3">Until</span>
                <TextInput type="datetime-local" value={toLocalInput(editing.body.ends_at)} onChange={(e) => e.target.value && set({ ends_at: fromLocalInput(e.target.value) })} block />
              </label>
            </div>
            <span className="font-mono text-xs text-fg4">A group stays in the sidebar and the group filter until its window ends; ended groups remain here.</span>
          </div>
        )}
      </Dialog>
      <Dialog open={!!assigning} onClose={() => setAssigning(null)} title={assigning ? `Units in ${assigning.name}` : ''} cancelLabel="Done">
        {assigning && (
          <div className="flex flex-col gap-2">
            {devs.length === 0 && <EmptyState label="No units enrolled yet" />}
            {devs.map((d) => {
              const on = d.assignment?.event_id === assigning.id;
              const elsewhere = !on && d.assignment?.event_name;
              return (
                <div key={d.id} className="flex items-center justify-between gap-3">
                  <Checkbox checked={on} onChange={(v) => void toggle(d, v)} label={d.name} disabled={events.busy} />
                  <span className="font-mono text-xs text-fg4">{elsewhere ? `IN ${String(elsewhere).toUpperCase()}` : on ? '' : 'UNASSIGNED'}</span>
                </div>
              );
            })}
          </div>
        )}
      </Dialog>
      <Dialog open={!!removing} onClose={() => setRemoving(null)} title={removing ? `Delete ${removing.name}?` : ''} confirm={{ label: 'Delete', destructive: true, onClick: remove, busy: events.busy }}>
        Units in this group are left unassigned. Recordings keep the group name.
      </Dialog>
    </>
  );
  if (embedded)
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-xs text-fg4">
            {list.length} GROUP{list.length === 1 ? '' : 'S'} · {list.filter((e) => groupState(e) === 'live').length} ACTIVE NOW
          </span>
          {newGroup}
        </div>
        <Card>{table}</Card>
        {dialogs}
      </div>
    );
  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Groups"
        meta={`${list.length} GROUPS · ${list.filter((e) => groupState(e) === 'live').length} ACTIVE NOW`}
        actions={
          <>
            {tabs}
            {newGroup}
          </>
        }
      />
      <div className="flex flex-col gap-3 md:hidden">
        {tabs}
        {newGroup}
      </div>
      <Card>{table}</Card>
      {dialogs}
    </Section>
  );
}

/** Edit groups from the fleet page: a frosted sheet over the blurred page with the full editor inside. */
export function GroupsDialog({ open, onClose, onOpenGroup }: { open: boolean; onClose: () => void; onOpenGroup?: (id: string) => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="Groups" meta="UNITS ARE GROUPED BY SHOW, ROOM OR CLIENT · A UNIT IS IN ONE GROUP AT A TIME" glass size="xl" cancelLabel="Close">
      <GroupsPanel embedded onOpenGroup={onOpenGroup ? (id) => { onClose(); onOpenGroup(id); } : undefined} />
    </Dialog>
  );
}
